import type { Hex } from "viem";

import { useCallback, useEffect, useRef, useState } from "react";

import { TX_STATUS_QUERY, gqlFetch } from "../lib/graphql.js";

/**
 * Encapsulates the `Query.txStatus` polling loop that drives the
 * `TxStatusPoller` UI. Keeping it out of the component drops the
 * component's cognitive complexity from ~40 (audit v7) to UI-only
 * rendering — the loop, terminal-state detection, cancellation token
 * and auto-start side-effect all live in one place that can be unit
 * tested separately.
 *
 * Tradeoff: the consumer no longer controls polling cadence (fixed at
 * 5s × 60 attempts here). When the playground needs configurable
 * intervals (e.g. fast-mode for local hardhat tests), pass them as
 * options instead of widening the surface.
 */

const POLL_INTERVAL_MS = 5_000;
const POLL_MAX_ATTEMPTS = 60;

export interface TxStatusSuccessShape {
  readonly __typename: "TxStatusSuccess";
  readonly status: "NOT_FOUND" | "INVALID" | "PENDING" | "DONE" | "FAILED";
  readonly substatus?: string;
  readonly substatusMessage?: string;
  readonly sending?: {
    readonly txHash: string;
    readonly chainId: number;
    readonly explorerUrl?: string;
    readonly amount?: string;
  };
  readonly receiving?: {
    readonly txHash: string;
    readonly chainId: number;
    readonly explorerUrl?: string;
    readonly amount?: string;
  };
  readonly tool?: string;
}

export interface TxStatusErrorShape {
  readonly __typename: "BadInputError" | "UpstreamError" | "CommonError";
  readonly message: string;
}

export type TxStatusPayload = TxStatusSuccessShape | TxStatusErrorShape;

export interface UseStatusPollingArgs {
  readonly endpoint: string;
  readonly apiKey: string;
  readonly fromChainId: number;
  readonly toChainId: number;
  /** Tx hash to track. The poller stops when undefined. */
  readonly hash: Hex | undefined;
}

export interface UseStatusPollingState {
  readonly status: TxStatusPayload | undefined;
  readonly attempt: number;
  readonly maxAttempts: number;
  readonly running: boolean;
  readonly error: string | undefined;
  readonly restart: () => void;
}

function isTerminalStatus(payload: TxStatusPayload | undefined): boolean {
  if (payload === undefined) return false;
  if (payload.__typename !== "TxStatusSuccess") return true;
  return (
    payload.status === "DONE" ||
    payload.status === "FAILED" ||
    payload.status === "INVALID"
  );
}

export function useStatusPolling(
  args: UseStatusPollingArgs,
): UseStatusPollingState {
  const { endpoint, apiKey, fromChainId, toChainId, hash } = args;
  const [status, setStatus] = useState<TxStatusPayload | undefined>(undefined);
  const [attempt, setAttempt] = useState(0);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const stopRef = useRef(false);

  const pollOnce = useCallback(
    async (h: Hex): Promise<TxStatusPayload | undefined> => {
      const { response } = await gqlFetch<{
        readonly txStatus: TxStatusPayload;
      }>({
        endpoint,
        apiKey,
        query: TX_STATUS_QUERY,
        operationName: "PlaygroundTxStatus",
        variables: {
          input: { hash: h, fromChain: fromChainId, toChain: toChainId },
        },
      });
      if (response.errors !== undefined && response.errors.length > 0) {
        setError(response.errors.map((e) => e.message).join(" · "));
        return undefined;
      }
      return response.data?.txStatus;
    },
    [endpoint, apiKey, fromChainId, toChainId],
  );

  const start = useCallback(async () => {
    if (hash === undefined) return;
    setRunning(true);
    setError(undefined);
    stopRef.current = false;
    setAttempt(0);
    setStatus(undefined);

    for (let n = 0; n < POLL_MAX_ATTEMPTS; n += 1) {
      if (stopRef.current) break;
      setAttempt(n + 1);
      try {
        const payload = await pollOnce(hash);
        setStatus(payload);
        if (isTerminalStatus(payload)) break;
      } catch (err) {
        setError(err instanceof Error ? err.message : String(err));
        break;
      }
      await new Promise((r) => setTimeout(r, POLL_INTERVAL_MS));
    }
    setRunning(false);
  }, [hash, pollOnce]);

  // Auto-start when `hash` populates. The `setTimeout(0)` keeps state
  // writes out of the same render that mounted the effect, which avoids
  // the `react-hooks/set-state-in-effect` cascade rule.
  useEffect(() => {
    if (hash === undefined) return undefined;
    const handle = setTimeout(() => {
      void start();
    }, 0);
    return () => {
      clearTimeout(handle);
      stopRef.current = true;
    };
  }, [hash, start]);

  return {
    status,
    attempt,
    maxAttempts: POLL_MAX_ATTEMPTS,
    running,
    error,
    restart: () => void start(),
  };
}
